import { CheckCircleOutlined, CloseOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Alert, Button } from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useParamCompilationStore } from './hooks/store/useParamCompilationStore.ts';

const AUTO_DISMISS_MS = 3_000;

export const ParamCompilationBanner: React.FC = () => {
  const compilationStatus = useParamCompilationStore(s => s.status);
  const dismiss = useParamCompilationStore(s => s.dismiss);
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when a new compilation starts
  useEffect(() => {
    if (compilationStatus === 'compiling') {
      setDismissed(false);
    }
  }, [compilationStatus]);

  // Auto-dismiss for success state
  useEffect(() => {
    if (compilationStatus === 'completed') {
      const timer = setTimeout(() => setDismissed(true), AUTO_DISMISS_MS);
      return () => clearTimeout(timer);
    }
  }, [compilationStatus]);

  // Clean up store after dismiss animation completes
  useEffect(() => {
    if (dismissed) {
      const timer = setTimeout(dismiss, 500);
      return () => clearTimeout(timer);
    }
  }, [dismissed, dismiss]);

  const visible = compilationStatus !== 'idle' && !dismissed;

  return (
    <div
      className={css`
        max-height: ${visible ? '300px' : 0};
        opacity: ${visible ? 1 : 0};
        transition-property: max-height, opacity;
        transition-duration: 0.5s;
        transition-timing-function: linear;
      `}
    >
      <Alert
        showIcon
        banner
        type={compilationStatus === 'timeout' ? 'warning' : compilationStatus === 'completed' ? 'success' : 'info'}
        icon={
          compilationStatus === 'compiling' ? (
            <LoadingOutlined />
          ) : compilationStatus === 'completed' ? (
            <CheckCircleOutlined />
          ) : (
            <WarningOutlined />
          )
        }
        title={
          compilationStatus === 'compiling'
            ? 'Parameter werden auf allen Instanzen zusammengestellt...'
            : compilationStatus === 'completed'
              ? 'Parameter wurden auf allen Instanzen aktualisiert.'
              : 'Parameter-Zusammenstellung dauert länger als erwartet. Bitte prüfen Sie den Status.'
        }
        action={
          compilationStatus !== 'compiling' ? (
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setDismissed(true)} />
          ) : null
        }
      />
    </div>
  );
};
